import React, { useEffect, useMemo, useState } from "react"
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Tooltip from "aldoo-ra/Tooltip"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { DashboardFilters } from "./api"
import { orgLogoUrl } from "aldoo-ra/Organizations/api"
import useDashboardTexts from "./useDashboardTexts"

/** An organization option (`org`: { logo, logoUpdatedAt }): its logo, or its initial. */
function OrgIcon({ id, org, name }) {
  const box = "w-6 h-6 rounded-full shrink-0"
  if (org.logo) return <img src={orgLogoUrl(id, org.logoUpdatedAt)} alt="" className={`${box} object-cover bg-admin_surface`} />
  return <span className={`${box} flex items-center justify-center bg-admin_primary/15 text-admin_primary text-[11px] font-semibold`}>{(name || "?").trim().charAt(0).toUpperCase()}</span>
}

/** How many values a dashboard's filters hold, all told. */
export const countFilters = (filters) => Object.values(filters || {}).reduce((n, values) => n + (values?.length || 0), 0)

/**
 * The filters this user may use for a scope (product, author, category, type,
 * and for the whole store its merchants), with their options — asked once per
 * scope, shared by the button and the chips.
 */
const cache = new Map()
export function useFilterOptions(scope) {
  const [defs, setDefs] = useState(() => cache.get(scope) || null)
  useEffect(() => {
    if (!scope) return
    if (cache.has(scope)) return setDefs(cache.get(scope))
    let alive = true
    DashboardFilters(scope)
      .then((res) => {
        const list = res?.filters || []
        cache.set(scope, list)
        if (alive) setDefs(list)
      })
      .catch(() => alive && setDefs([]))
    return () => {
      alive = false
    }
  }, [scope])
  return defs
}

/**
 * "Filter by", the way App Store Connect has it: a button beside the dates
 * that says how many filters are on, and a panel to choose them — several
 * values of each, any number of them together — cleared all at once.
 */
export default function Filters({ scope, value, onChange }) {
  const t = useDashboardTexts()
  const defs = useFilterOptions(scope)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value || {})
  useEffect(() => {
    if (open) setDraft(value || {})
  }, [open])
  const count = countFilters(value)
  if (defs && !defs.length) return null

  const setValues = (id, values) =>
    setDraft((d) => {
      const next = { ...d }
      if (values.length) next[id] = values
      else delete next[id]
      return next
    })

  return (
    <>
      {/* the name in a tooltip while the button is the funnel alone (below 2xl) */}
      <Tooltip text={t`Filters`} position="bottom" variant="admin" className="2xl:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t`Filters`}
        className={`relative flex items-center gap-2 h-10 px-2.5 2xl:px-3 rounded-lg border text-sm font-semibold ${count ? "border-admin_primary bg-admin_primary/10 text-admin_primary" : "border-admin_bordered bg-admin_paper text-admin_text hover:border-admin_primary"}`}
        data-testid="dashboard-filters"
      >
        <FunnelIcon className="w-5 h-5" />
        {/* below 2xl the funnel alone (its count beside it when filters are on): the toolbar wraps otherwise */}
        <span className="hidden 2xl:inline">{t`Filters`}</span>
        {count > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-admin_primary text-white text-[11px] font-bold tabular-nums" data-testid="dashboard-filters-count">
            {count}
          </span>
        )}
      </button>
      </Tooltip>

      <Modal isOpen={open} onClose={() => setOpen(false)} variant="admin_drawer_right" title={t`Filter by`} closeOnEscape className="max-w-[440px]">
        <div className="flex flex-col h-full" data-testid="dashboard-filters-panel">
          <div className="flex-1 flex flex-col gap-5 p-4 overflow-y-auto">
            {!defs && <span className="text-sm text-admin_text_secondary">{t`Loading…`}</span>}
            {(defs || []).map((def) => {
              const chosen = draft[def.id] || []
              const byValue = new Map(def.options.map((o) => [o.value, o]))
              return (
                <div key={def.id} className="flex flex-col gap-1.5" data-testid={`filter-${def.id}`}>
                  <span className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">
                    {t(def.label)}
                    {chosen.length > 0 && (
                      <button type="button" onClick={() => setValues(def.id, [])} className="normal-case tracking-normal font-semibold text-admin_primary hover:underline">
                        {t`Clear`}
                      </button>
                    )}
                  </span>
                  <DropDownSelect
                    variant="admin"
                    multiSelection
                    searchEnabled
                    showSelectAll={false}
                    enableResetButton={false}
                    nameField="label"
                    placeholder={t("All %ss", t(def.label).toLowerCase())}
                    options={def.options}
                    value={chosen.map((v) => byValue.get(v) || { value: v, label: v })}
                    onChange={(sel) => setValues(def.id, (Array.isArray(sel) ? sel : sel ? [sel] : []).map((o) => o.value))}
                    onItemRender={(o) => (
                      <span className="flex items-center gap-2 py-0.5 min-w-0">
                        {o.org && <OrgIcon id={o.value} org={o.org} name={o.label} />}
                        <span className="flex flex-col min-w-0">
                          <span className="text-sm text-admin_text truncate">{o.label}</span>
                          {o.hint && o.hint !== o.label && <span className="text-[11px] text-admin_text_secondary truncate">{o.hint}</span>}
                        </span>
                      </span>
                    )}
                    onValueItemRender={(sel) => {
                      const list = Array.isArray(sel) ? sel : sel ? [sel] : []
                      if (!list.length) return <span className="text-sm text-admin_text_secondary">{t`All`}</span>
                      return <span className="text-sm truncate">{list.length === 1 ? list[0].label : t("%s selected", list.length)}</span>
                    }}
                  />
                  {chosen.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {chosen.map((v) => (
                        <FilterChip key={v} label={byValue.get(v)?.label || v} removeTitle={t`Remove`} onRemove={() => setValues(def.id, chosen.filter((x) => x !== v))} />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <div className="flex items-center justify-between gap-2 p-4 border-t border-admin_bordered">
            <Button variant="admin_secondary" onClick={() => setDraft({})} disabled={!countFilters(draft)} data-testid="dashboard-filters-clear">
              {t`Clear all`}
            </Button>
            <Button
              variant="admin_primary"
              onClick={() => {
                onChange(draft)
                setOpen(false)
              }}
              data-testid="dashboard-filters-apply"
            >
              {t`Apply`}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

const FilterChip = ({ label, prefix, removeTitle, onRemove }) => (
  <span className="inline-flex items-center gap-1 max-w-full h-7 pl-2.5 pr-1 rounded-full bg-admin_primary/10 text-admin_primary text-xs font-semibold" data-testid="filter-chip">
    <span className="truncate">
      {prefix && <span className="font-normal text-admin_text_secondary">{prefix}: </span>}
      {label}
    </span>
    <button type="button" onClick={onRemove} className="p-0.5 rounded-full hover:bg-admin_primary/15" title={removeTitle}>
      <XMarkIcon className="w-3.5 h-3.5" />
    </button>
  </span>
)

/** The filters that are on, as chips under the toolbar: each removed on its own, or all at once. */
export function ActiveFilters({ scope, value, onChange }) {
  const t = useDashboardTexts()
  const defs = useFilterOptions(scope)
  const labels = useMemo(() => new Map((defs || []).map((d) => [d.id, { label: d.label, names: new Map(d.options.map((o) => [o.value, o.label])) }])), [defs])
  const entries = Object.entries(value || {}).flatMap(([id, values]) => (values || []).map((v) => ({ id, v })))
  if (!entries.length) return null
  const remove = (id, v) => {
    const next = { ...value, [id]: value[id].filter((x) => x !== v) }
    if (!next[id].length) delete next[id]
    onChange(next)
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="dashboard-active-filters">
      {entries.map(({ id, v }) => (
        <FilterChip key={`${id}:${v}`} prefix={t(labels.get(id)?.label) || id} label={labels.get(id)?.names.get(v) || v} removeTitle={t`Remove`} onRemove={() => remove(id, v)} />
      ))}
      <button type="button" onClick={() => onChange({})} className="ml-1 text-xs font-semibold text-admin_primary hover:underline">
        {t`Clear all`}
      </button>
    </div>
  )
}
